import {
  Filter,
  FilterValue,
  InputFilter,
  PeriodFilterValue,
  StringArrayFilterValue,
} from "../../types/filter";
import { isGroupFilter } from "./filter";

export function isStringArrayFilterValue(
  value: FilterValue
): value is StringArrayFilterValue {
  return Array.isArray(value.value);
}

function isCustomPeriodFilterValue(
  value: FilterValue
): value is PeriodFilterValue {
  return (
    typeof (value as PeriodFilterValue).startDate === "string" &&
    typeof (value as PeriodFilterValue).endDate === "string"
  );
}

export function getSelectedFilterValue(
  filterId: string,
  filterValues: FilterValue[],
  defaultValue: FilterValue
): FilterValue {
  const filterValue = filterValues.find(
    (filterValue) => filterId === filterValue.filterId
  );
  if (filterValue && typeof filterValue?.value === typeof defaultValue?.value) {
    return filterValue;
  } else if (defaultValue) {
    return defaultValue;
  } else {
    return {
      filterId: filterId,
      value: null,
    };
  }
}

function findValueIsInFilterValues(
  currentValues: FilterValue[],
  filterId: string
) {
  return currentValues.find(
    (currentValue) => filterId === currentValue.filterId
  );
}

export function valueIsInFilterValues(
  currentValues: FilterValue[],
  filterId: string
) {
  return !!findValueIsInFilterValues(currentValues, filterId);
}

export function updatedFilterValueFromValues(
  currentValues: FilterValue[],
  filter: InputFilter,
  newValue: FilterValue
) {
  if (filter.id !== newValue.filterId) {
    console.log("Internal error. filter.id and newFilter.filterId must match.");
    return currentValues;
  }
  // Do not save default values
  if (filterValueAreTheSame(newValue, filter.defaultValue)) {
    return removeFilterValueFromValues(currentValues, filter.id);
  }

  return [
    ...currentValues.filter(
      (currentValue) =>
        filter.id !== currentValue.filterId &&
        filter.id !== currentValue.dependsOnFilterId
    ),
    newValue,
  ];
}

export function removeFilterValueFromValues(
  currentValues: FilterValue[],
  filterId: string
) {
  if (!valueIsInFilterValues(currentValues, filterId)) {
    return currentValues;
  } else {
    return [
      ...currentValues.filter(
        (currentValue) =>
          filterId !== currentValue.filterId &&
          filterId !== currentValue.dependsOnFilterId
      ),
    ];
  }
}
export function stringArrayAreTheSame(values1: string[], values2: string[]) {
  if (values1.length !== values2.length) {
    return false;
  } else {
    values1.sort((value1, value2) => value1.localeCompare(value2));
    values2.sort((value1, value2) => value1.localeCompare(value2));
    for (let idx = 0; idx < values1.length; idx++) {
      if (values1[idx] !== values2[idx]) {
        return false;
      }
    }
    return true;
  }
}

function filterValueAreTheSame(values1: FilterValue, values2: FilterValue) {
  if (values1.filterId !== values2.filterId) {
    return false;
  } else {
    if (
      isCustomPeriodFilterValue(values1) &&
      isCustomPeriodFilterValue(values2)
    ) {
      return (
        values1.value === values2.value &&
        values1.startDate === values2.startDate &&
        values1.endDate === values2.endDate
      );
    } else if (
      isStringArrayFilterValue(values1) &&
      isStringArrayFilterValue(values2)
    ) {
      return stringArrayAreTheSame(values1.value, values2.value);
    } else if (values1.value !== values2.value) {
      return false;
    }
  }
  return true;
}

export function filterValuesAreTheSame(
  values1: FilterValue[],
  values2: FilterValue[]
) {
  if (values1.length !== values2.length) {
    return false;
  } else {
    values1.sort((value1, value2) =>
      value1.filterId.localeCompare(value2.filterId)
    );
    values2.sort((value1, value2) =>
      value1.filterId.localeCompare(value2.filterId)
    );
    for (let idx = 0; idx < values1.length; idx++) {
      if (!filterValueAreTheSame(values1[idx]!, values2[idx]!)) {
        return false;
      }
    }
    return true;
  }
}

export const symmetricDifference = (
  filterValueA: string[],
  filterValueB: string[]
) => {
  const setA = new Set(filterValueA);
  const setB = new Set(filterValueB);

  const diffA = Array.from(setA).filter((value) => !setB.has(value));
  const diffB = Array.from(setB).filter((value) => !setA.has(value));

  return [...diffA, ...diffB];
};

// Counts the number of values that are not default values
export function getInputFilterValueCount(
  filterValueA: FilterValue,
  filterValueB: FilterValue
): number {
  if (
    isStringArrayFilterValue(filterValueA) &&
    isStringArrayFilterValue(filterValueB)
  ) {
    const diff = symmetricDifference(filterValueA.value, filterValueB.value);
    return diff.length;
  } else {
    return filterValueA.value === filterValueB.value ? 0 : 1;
  }
}

export function getFilterValueCount(
  filter: Filter,
  filterValues: FilterValue[]
): number {
  if (isGroupFilter(filter)) {
    return filter.filters.reduce((accumulator, groupFilter) => {
      return accumulator + getFilterValueCount(groupFilter, filterValues);
    }, 0);
  } else {
    const { id, defaultValue } = filter;
    const value = getSelectedFilterValue(id, filterValues, defaultValue);
    return getInputFilterValueCount(value, defaultValue);
  }
}
